import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Save, Facebook, Instagram, Youtube, Twitter, Linkedin, Globe } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface SocialLink {
  id?: string;
  platform: string;
  url: string;
  display_order: number;
  is_active: boolean;
}

interface SocialLinksManagerProps {
  ptaId: string;
}

const platformOptions = [
  { value: 'facebook', label: 'Facebook', icon: Facebook },
  { value: 'instagram', label: 'Instagram', icon: Instagram },
  { value: 'twitter', label: 'X (Twitter)', icon: Twitter },
  { value: 'youtube', label: 'YouTube', icon: Youtube },
  { value: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { value: 'website', label: 'Website', icon: Globe },
];

export function SocialLinksManager({ ptaId }: SocialLinksManagerProps) {
  const [links, setLinks] = useState<SocialLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLinks();
  }, [ptaId]);

  const fetchLinks = async () => {
    const { data, error } = await supabase
      .from('pta_social_links')
      .select('*')
      .eq('pta_id', ptaId)
      .order('display_order', { ascending: true });

    if (data) {
      setLinks(data);
    }
    setLoading(false);
  };

  const handleAddLink = () => {
    // Find first unused platform
    const usedPlatforms = links.map(l => l.platform);
    const availablePlatform = platformOptions.find(p => !usedPlatforms.includes(p.value));
    
    if (!availablePlatform) {
      toast({
        title: "All platforms added",
        description: "You've already added all available social platforms",
        variant: "destructive"
      });
      return;
    }

    setLinks([
      ...links,
      {
        platform: availablePlatform.value,
        url: "",
        display_order: links.length,
        is_active: true
      }
    ]);
  };

  const handleRemoveLink = async (index: number) => {
    const link = links[index];
    setLinks(links.filter((_, i) => i !== index));
    
    if (link.id) {
      await supabase.from('pta_social_links').delete().eq('id', link.id);
    }
  };

  const handleUpdateLink = (index: number, field: 'platform' | 'url', value: string) => {
    const updated = [...links];
    updated[index] = { ...updated[index], [field]: value };
    setLinks(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Delete all existing links for this PTA
      await supabase.from('pta_social_links').delete().eq('pta_id', ptaId);

      // Insert all current links (filter out empty URLs)
      const linksToInsert = links
        .filter(link => link.url.trim() !== '')
        .map((link, index) => ({
          pta_id: ptaId,
          platform: link.platform,
          url: link.url,
          display_order: index,
          is_active: true
        }));

      if (linksToInsert.length > 0) {
        const { error } = await supabase.from('pta_social_links').insert(linksToInsert);
        if (error) throw error;
      }

      toast({
        title: "Social links saved",
        description: "Your social media links have been updated"
      });
      fetchLinks();
    } catch (error) {
      console.error('Error saving social links:', error);
      toast({
        title: "Error",
        description: "Failed to save social links",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const getIcon = (platform: string) => {
    const option = platformOptions.find(p => p.value === platform);
    if (option) {
      const Icon = option.icon;
      return <Icon className="h-4 w-4" />;
    }
    return <Globe className="h-4 w-4" />;
  };

  if (loading) {
    return <div className="text-center py-4 text-muted-foreground">Loading social links...</div>;
  }

  return (
    <Card className="shadow-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Social Media Links</CardTitle>
            <CardDescription>
              Add social media links to display in your fundraising page footer
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={handleAddLink}>
            <Plus className="h-4 w-4 mr-1" />
            Add Link
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {links.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No social media links configured yet.</p>
            <p className="text-sm">Click "Add Link" to add your social media profiles.</p>
          </div>
        ) : (
          links.map((link, index) => (
            <div key={index} className="flex items-center gap-3 p-3 border border-border rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground">
                {getIcon(link.platform)}
              </div>
              <Select
                value={link.platform}
                onValueChange={(value) => handleUpdateLink(index, 'platform', value)}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {platformOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <option.icon className="h-4 w-4" />
                        {option.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="flex-1"
                value={link.url}
                onChange={(e) => handleUpdateLink(index, 'url', e.target.value)}
                placeholder={`https://${link.platform}.com/your-page`}
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleRemoveLink(index)}
              >
                <Trash2 className="h-4 w-4 text-foreground" />
              </Button>
            </div>
          ))
        )}

        {links.length > 0 && (
          <Button onClick={handleSave} disabled={saving} className="w-full">
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save Social Links"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}